import {
  CHANGE_CONNECTION,
  Connection,
  GET_ALL_CONNECTIONS,
  InputMaybe,
} from '@/rtk-query.gql';
import { getLocalStorage } from '@/utils/storage';

import { enhancedApi } from '../api/baseApi';
import { ConnectionsActions } from '../slices/connections';
import { addToast } from '../slices/toast';
import { RootState, store } from '../store';

type ChangeConnectionVariables = {
  environmentId: number;
  b2c: InputMaybe<boolean>;
};

export const connectionsApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getAllConnections: build.query<Connection[], void>({
      query: () => ({
        document: GET_ALL_CONNECTIONS,
      }),
      providesTags: ['Connections'],
      transformResponse: (response: { allConnections: Connection[] }) => {
        return response.allConnections.map((connection: Connection) => ({
          ...connection,
          languageCode:
            connection.languageCode?.toLowerCase() === 'en-us'
              ? 'en'
              : connection.languageCode?.toLowerCase(),
        }));
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: allConnections } = await queryFulfilled;

          const state = store.getState() as RootState;
          const environmentId = state.authentication.me?.environmentId;

          const currentConnection = allConnections.find(
            (connection) => connection.overrideEnvironmentId === environmentId,
          );

          const allRegions = allConnections
            .filter(
              (connection) =>
                connection.connectionType === 'Production' &&
                connection.overrideEnvironmentId !== 0,
            )
            .map((connection: Connection) => ({
              countryName: connection.countryName,
              countryCode: connection.countryCode,
              languageCode: connection.languageCode,
            }));

          const currentRegion = allRegions.find(
            (region) => region.countryCode === currentConnection?.countryCode,
          );

          dispatch({
            type: ConnectionsActions.UPDATE_CONNECTIONS,
            allConnections,
            currentConnection,
            allRegions,
            currentRegion,
          });
        } catch (error) {
          console.error(error);
        }
      },
    }),
    changeConnection: build.mutation({
      query: (variables: ChangeConnectionVariables) => ({
        document: CHANGE_CONNECTION,
        variables,
      }),
      async onQueryStarted(_variables, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          const state = getState() as RootState;
          const allConnections = state.connections.allConnections;

          const currentConnection = allConnections.find(
            (connection: Connection) =>
              connection.overrideEnvironmentId === data.changeConnection,
          );

          if (currentConnection) {
            dispatch({
              type: ConnectionsActions.UPDATE_CURRENT_CONNECTION,
              currentConnection,
            });

            // Update current region if needed
            const currentRegion = {
              countryName: currentConnection.countryName,
              countryCode: currentConnection.countryCode,
              languageCode: currentConnection.languageCode,
            };

            dispatch({
              type: ConnectionsActions.UPDATE_CURRENT_REGION,
              currentRegion: getLocalStorage('region') || currentRegion,
            });
          }
        } catch (error: any) {
          console.error('Error changing connection:', error);

          const { code, message } =
            error.meta?.response?.errors?.[0]?.extensions ?? {};
          dispatch(
            addToast({
              severity: 'error',
              summary: code || 'Error changing connections',
              detail:
                message ||
                'An error occurred while changing connections. Please try again.',
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useGetAllConnectionsQuery,
  useLazyGetAllConnectionsQuery,
  useChangeConnectionMutation,
} = connectionsApi;
